import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  uploadZone: {
    border: `1.5px dashed ${theme.palette.divider}`,
    padding: theme.spacing(4, 3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(0,0,0,0.015)",
    },
    "&.drag-over": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(0,0,0,0.025)",
    },
  },
  uploadIcon: {
    fontSize: 36,
    color: theme.palette.text.disabled,
    margin: "auto",
    marginBottom: theme.spacing(1),
    display: "block",
  },
  uploadTitle: {
    fontSize: "0.9375rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  uploadSubtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  browseLink: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  },
  fileList: {
    marginTop: theme.spacing(2),
    padding: 0,
  },
  fileListItem: {
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: theme.palette.background.default,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  fileIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
  },
  fileName: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "75%",
  },
  fileSize: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  removeButton: {
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.06)",
    },
  },
  removeIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  helperText: {
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  errorText: {
    color: theme.palette.error.main,
  },
  hiddenInput: {
    display: "none",
  },
  fileItemInner: {
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
}));

const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const MAX_FILE_SIZE_MB = 10;

const DocumentUpload = ({ field, files, onFilesChange }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState([]);

  const validateAndAddFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);
    const errs = [];
    const valid = [];

    fileArray.forEach((file) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errs.push(`"${file.name}" exceeds the 10MB size limit.`);
      } else {
        valid.push(file);
      }
    });

    setErrors(errs);
    if (valid.length > 0) {
      onFilesChange([...files, ...valid]);
    }
  };

  const handleInputChange = (e) => {
    validateAndAddFiles(e.target.files);
    e.target.value = ""; // reset so same file can be re-added
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndAddFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleRemove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onFilesChange(updated);
  };

  return (
    <Box>
      {/* Drop Zone */}
      <Box
        className={`${classes.uploadZone} ${dragOver ? "drag-over" : ""}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          className={classes.hiddenInput}
          accept={field.accept}
          multiple={field.multiple}
          onChange={handleInputChange}
        />
        <CloudUploadIcon className={classes.uploadIcon} />
        <Typography className={classes.uploadTitle}>
          {field.dropLabel || "Drag & drop files here, or"}{" "}
          <span className={classes.browseLink}>browse</span>
        </Typography>
        {field.helperText && (
          <Typography className={classes.uploadSubtitle}>
            {field.helperText}
          </Typography>
        )}
      </Box>

      {/* Error messages */}
      {errors.map((err, i) => (
        <FormHelperText key={i} className={`${classes.helperText} ${classes.errorText}`}>
          {err}
        </FormHelperText>
      ))}

      {/* File list */}
      {files.length > 0 && (
        <List className={classes.fileList} disablePadding>
          {files.map((file, index) => (
            <ListItem key={index} className={classes.fileListItem} disableGutters>
              <Box className={classes.fileItemInner}>
                <InsertDriveFileIcon className={classes.fileIcon} />
                <Box style={{ overflow: "hidden" }}>
                  <Typography className={classes.fileName}>{file.name}</Typography>
                  <Typography className={classes.fileSize}>
                    {formatBytes(file.size)}
                  </Typography>
                </Box>
              </Box>
              <ListItemSecondaryAction>
                <IconButton
                  className={classes.removeButton}
                  onClick={() => handleRemove(index)}
                  aria-label={`Remove ${file.name}`}
                  edge="end"
                  size="small"
                >
                  <CloseIcon className={classes.removeIcon} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default React.memo(DocumentUpload);
